import { Injectable } from '@angular/core';
import { BullhornEntityTypes, BullhornNamespace } from 'datasource-service-lib';
import {
  DocumentDestinationActionProperties,
  IDataSourceApiService,
  IDocumentDestinationService,
  IEntity,
  INamedEntity,
  TemplateWorkflowConfig,
} from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';

interface IAliasedEntity extends IEntity {
  name?: string;
  alias?: string;
}

@Injectable({ providedIn: 'root' })
export class BullhornDocumentDestinationService implements IDocumentDestinationService {
  private static ENTITIES_REQUIRING_EXTRA_FIELDS: string[] = [
    BullhornEntityTypes.JobOrder,
    BullhornEntityTypes.JobSubmission,
    BullhornEntityTypes.Placement,
    BullhornEntityTypes.ClientContact,
    BullhornEntityTypes.Opportunity,
  ];

  private readonly CLIENT_SETTINGS_FIELDS = ['leadAndOpportunityEnabled'];

  private apiService: IDataSourceApiService;

  clientLeadOpportunityEnabled: boolean | undefined = undefined;

  private entityQueryFieldsMap = {
    [BullhornEntityTypes.JobOrder]: [
      'clientContact(id,name)',
      'clientCorporation(id,name)',
      'opportunity(id,title)'],
    [BullhornEntityTypes.JobSubmission]: [
      'candidate(id,firstName,lastName)',
      'jobOrder(id,title,clientContact(id,name),clientCorporation(id,name))',
    ],
    [BullhornEntityTypes.Placement]: [
      'candidate(id,firstName,lastName)',
      'clientCorporation(id,name)',
      'vendorClientCorporation(id,name)',
      'jobOrder(id,title,clientContact(id,name))',
    ],
    [BullhornEntityTypes.ClientContact]: ['clientCorporation(id,name)'],
    [BullhornEntityTypes.Opportunity]: ['clientContact(id,name)', 'clientCorporation(id,name)'],
  };

  constructor(
    private messageNotificationService: MessageNotificationService,
    sps: SharedServiceProviderServiceFactory,
  ) {
    this.apiService = sps.getServiceProviderService(BullhornNamespace.name).getDataSourceApiService();
  }

  getDocumentDestinations(
    entity: IEntity,
    template: TemplateWorkflowConfig,
    dialogResult?: IEntity[]
  ): Observable<IEntity[]> {
    return this.loadClientSettings().pipe(
      switchMap(() => {
        const entityTypes: string[] | undefined = this.getDestinationEntityTypesFromTemplateConfig(entity.type, template);
        const possibleDestinations: IEntity[] = [entity, ...(dialogResult?.length ? dialogResult : [])];
        if (!entityTypes) {
          // this applies previous bullhorn specific logic for old templates without destination config
          if (entity.type === BullhornEntityTypes.ClientCorporation) {
            return of([entity]);
          }
          return of(possibleDestinations);
        }

        if (!BullhornDocumentDestinationService.ENTITIES_REQUIRING_EXTRA_FIELDS.includes(entity.type)) {
          return of(this.filterOnConfigDestinations(possibleDestinations, entityTypes));
        }

        return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(
          map((entityFields) => {
            const extraEntities = this.destinationEntities(entityFields);
            const destinations: IAliasedEntity[] = [...possibleDestinations, ...extraEntities];
            return this.filterOnConfigDestinations(destinations, entityTypes);
          }),
        );
      }),
    );
  }

  private filterOnConfigDestinations(destinations: IAliasedEntity[], entityTypes: string[]) {
    return destinations.filter((x) => {
      const filterBy = x.alias?.toLowerCase() ?? x.type.toLowerCase();
      return entityTypes.map((y) => y.toLowerCase()).includes(filterBy);
    }) as IEntity[];
  }

  private getFields(entity: IEntity): string {
    let fields = [];
    switch (entity.type) {
      case BullhornEntityTypes.JobOrder:
        fields = this.entityQueryFieldsMap[BullhornEntityTypes.JobOrder];
        break;
      case BullhornEntityTypes.JobSubmission:
        fields = this.entityQueryFieldsMap[BullhornEntityTypes.JobSubmission];
        break;
      case BullhornEntityTypes.Placement:
        fields = this.entityQueryFieldsMap[BullhornEntityTypes.Placement];
        break;
      case BullhornEntityTypes.ClientContact:
        fields = this.entityQueryFieldsMap[BullhornEntityTypes.ClientContact];
        break;
      case BullhornEntityTypes.Opportunity:
        fields = this.entityQueryFieldsMap[BullhornEntityTypes.Opportunity];
        break;
      default:
        throw new Error(`${entity.type} is not in scope for document destination extra fields.`);
    }
    fields = this.cleanupFields(entity.type, fields);
    return fields.join(',');
  }

  private destinationEntities(entityData: any): IAliasedEntity[] {
    const entities: IAliasedEntity[] = [];
    switch (entityData.type) {
      case BullhornEntityTypes.JobOrder:
        if (entityData.clientContact) {
          entities.push(
            this.entityMappers({
              ...entityData.clientContact,
              type: BullhornEntityTypes.ClientContact,
            }),
          );
        }
        if (entityData.clientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.clientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
            }),
          );
        }
        if (entityData.opportunity && this.clientLeadOpportunityEnabled) {
          entities.push(
            this.entityMappers({
              ...entityData.opportunity,
              type: BullhornEntityTypes.Opportunity,
            }),
          );
        }
        break;
      case BullhornEntityTypes.JobSubmission:
        if (entityData.candidate) {
          entities.push(
            this.entityMappers({
              ...entityData.candidate,
              type: BullhornEntityTypes.Candidate,
            }),
          );
        }
        if (entityData.jobOrder) {
          entities.push(
            this.entityMappers({
              ...entityData.jobOrder,
              type: BullhornEntityTypes.JobOrder,
            }),
          );
        }
        if (entityData.jobOrder?.clientContact) {
          entities.push(
            this.entityMappers({
              ...entityData.jobOrder.clientContact,
              type: BullhornEntityTypes.ClientContact,
            }),
          );
        }
        if (entityData.jobOrder?.clientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.jobOrder.clientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
            }),
          );
        }
        break;
      case BullhornEntityTypes.Placement:
        if (entityData.candidate) {
          entities.push(
            this.entityMappers({
              ...entityData.candidate,
              type: BullhornEntityTypes.Candidate,
            }),
          );
        }
        if (entityData.clientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.clientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
            }),
          );
        }
        if (entityData.vendorClientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.vendorClientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
              alias: 'vendorClientCorporation',
            }),
          );
        }
        if (entityData.jobOrder) {
          entities.push(
            this.entityMappers({
              ...entityData.jobOrder,
              type: BullhornEntityTypes.JobOrder,
            }),
          );
        }
        if (entityData.jobOrder?.clientContact) {
          entities.push(
            this.entityMappers({
              ...entityData.jobOrder?.clientContact,
              type: BullhornEntityTypes.ClientContact,
            }),
          );
        }
        break;
      case BullhornEntityTypes.ClientContact:
        if (entityData.clientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.clientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
            }),
          );
        }
        break;
      case BullhornEntityTypes.Opportunity:
        if (entityData.clientContact) {
          entities.push(
            this.entityMappers({
              ...entityData.clientContact,
              type: BullhornEntityTypes.ClientContact,
            }),
          );
        }
        if (entityData.clientCorporation) {
          entities.push(
            this.entityMappers({
              ...entityData.clientCorporation,
              type: BullhornEntityTypes.ClientCorporation,
            }),
          );
        }
        break;
      default:
        throw new Error(`${entityData.type} is not in scope for document destination extra fields.`);
    }
    return entities;
  }

  private entityMappers(entityData: any): IAliasedEntity {
    const entity: IAliasedEntity = {
      id: entityData.id.toString(),
      type: entityData.type,
      alias: entityData.alias,
      name: '', // Each return overrides this value
    };
    switch (entityData.type) {
      case BullhornEntityTypes.Candidate:
        return {
          ...entity,
          name: `${entityData.firstName} ${entityData.lastName}`,
        } as INamedEntity;
      case BullhornEntityTypes.ClientCorporation:
        return { ...entity, name: entityData.name } as INamedEntity;
      case BullhornEntityTypes.JobOrder:
        return {
          ...entity,
          name: `${entityData.title ? entityData.title : ''}`,
        } as INamedEntity;
      case BullhornEntityTypes.ClientContact:
        return { ...entity, name: entityData.name } as INamedEntity;
      case BullhornEntityTypes.Opportunity:
        return {
          ...entity,
          name: `${entityData.title ? entityData.title : ''}`,
        } as INamedEntity;
      default:
        throw new Error(`${entityData.type} is not in scope for document destination extra fields.`);
    }
  }

  private getDestinationEntityTypesFromTemplateConfig(entityType: string, template: TemplateWorkflowConfig): string[] | undefined {
    const actions = template.actionConfigs.filter((x) => x.type === 'documentDestination');
    if (!actions || !actions.length) {
      return undefined;
    }

    const properties = this.findDestinationActionProperties(actions, entityType);
    if (!properties || !properties.paths) {
      return [];
    }

    return properties.paths.map((x) => this.pathDisplay(x));
  }

  private findDestinationActionProperties(actions: TemplateWorkflowConfig['actionConfigs'],
                                          entityType: string): DocumentDestinationActionProperties | undefined {
    const exactMatch = actions.find((x) => (x.properties as DocumentDestinationActionProperties).entityType === entityType);
    if (exactMatch) {
      return exactMatch.properties as DocumentDestinationActionProperties;
    }

    const pathPrefixMatch = actions.find((x) => {
      const props = x.properties as DocumentDestinationActionProperties;
      return props.paths?.some((path) => path.startsWith(`${entityType}.`));
    });
    return pathPrefixMatch?.properties as DocumentDestinationActionProperties | undefined;
  }

  private pathDisplay(path: string) {
    return path.split('.').slice(-1).toString();
  }

  private cleanupFields(entity: string, fields: string[]): string[] {
    if (entity === BullhornEntityTypes.JobOrder && !this.clientLeadOpportunityEnabled) {
      return fields.filter((x) => !x.includes('opportunity'));
    }
    return fields;
  }

  private loadClientSettings(): Observable<any> {
    if (this.clientLeadOpportunityEnabled === undefined) {
      return this.apiService.settings(this.CLIENT_SETTINGS_FIELDS.join(',')).pipe(
        tap((response) => {
          if (response?.leadAndOpportunityEnabled !== undefined && response?.leadAndOpportunityEnabled !== null) {
            this.clientLeadOpportunityEnabled = response.leadAndOpportunityEnabled;
          }
        }),
        catchError((error) => {
          this.messageNotificationService.error('Failed to load client settings', error);
          return of(null);
        }),
      );
    }
    return of(null);
  }
}
